import inputStyles from "../StyledInput/StyledInput.module.css";
import classNames from "classnames";
import { ComponentProps, useEffect, useState } from "react";
import dropDownIcon from "../../../../assets/drop-down.svg";
import styles from "./Select.module.css";
import InputValidationMessages from "../InputValidationMessages/InputValidationMessages";
import {
  StyledInputLabelProps,
  StyledInputValidationProps,
} from "../StyledInput/StyledInput";

type SelectDisabledProps<T> =
  | {
      disabled: true;
      onChange?: never;
    }
  | { disabled?: boolean; onChange: (value: T) => void };

type Props<T extends string> = Omit<ComponentProps<"select">, "onChange"> & {
  selectedLabelClassName?: string;
  innerContentClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  options: Array<{
    key?: string;
    label: string;
    value: T;
  }>;
  placeholder?: string;
  selectedValue?: T;
  showPopover?: boolean;
  variant?: "default" | "horizontal";
} & StyledInputLabelProps &
  StyledInputValidationProps &
  SelectDisabledProps<T>;

export default function Select<T extends string>({
  className,
  selectedLabelClassName,
  innerContentClassName,
  labelClassName,
  inputClassName,
  options,
  onChange,
  disabled,
  selectedValue,
  placeholder,
  label,
  "aria-label": ariaLabel,
  showPopover = true,
  validationMessages,
  invalid = !!validationMessages && validationMessages.length > 0,
  variant = "default",
  ...rest
}: Props<T>) {
  const [hidePopoverOverride, setHidePopoverOverride] = useState(false);

  const selectedOptionIndex = options.findIndex(
    (option) => option.value === selectedValue,
  );

  useEffect(() => {
    setHidePopoverOverride(false);
  }, [validationMessages]);

  return (
    <div className={classNames(inputStyles.container, className)}>
      <label
        className={classNames(
          {
            [inputStyles.label]: true,
            [styles.disabled]: disabled,
            [inputStyles.labelHorizontal]: variant === "horizontal",
          },
          innerContentClassName,
        )}
        aria-label={ariaLabel}
      >
        {label && <span className={labelClassName}>{label}</span>}
        <div
          className={classNames(
            {
              [inputStyles.inputContainer]: true,
              [inputStyles.inputContainerHorizontal]: variant === "horizontal",
            },
            inputClassName,
          )}
        >
          <div className={styles.selectContainer}>
            <select
              {...rest}
              className={classNames({
                [styles.select]: true,
                [styles.invalid]: invalid,
              })}
              value={selectedValue}
              onChange={(e) => {
                const value = e.target.value as T;
                if (value !== placeholder) {
                  onChange?.(value);
                }
              }}
              disabled={disabled}
              onFocus={() => setHidePopoverOverride(false)}
              key={selectedValue ? "withValue" : "noValue"}
            >
              {placeholder ? (
                <option className={styles.options} value={placeholder}>
                  {placeholder}
                </option>
              ) : null}
              {options.map((option) => (
                <option
                  key={option.key ?? option.value}
                  value={option.value}
                  className={styles.options}
                >
                  {option.label}
                </option>
              ))}
            </select>
            <div className={styles.content}>
              <div
                className={classNames(
                  styles.selectedLabel,
                  selectedLabelClassName,
                )}
                aria-hidden={true}
              >
                {selectedOptionIndex !== -1
                  ? options[selectedOptionIndex].label
                  : placeholder}
              </div>
              <img src={dropDownIcon} alt="" aria-hidden={true} />
            </div>
          </div>
          <InputValidationMessages
            showPopover={showPopover}
            hidePopoverOverride={hidePopoverOverride}
            setHidePopoverOverride={setHidePopoverOverride}
            validationMessages={validationMessages}
          />
        </div>
      </label>
    </div>
  );
}
